import { type ComponentProps, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { BwCloseLine, BwLoadingLine } from "@/components/ui/icons";
import {
  AudioRecordPlayer,
  AudioRecordPlayerDuration,
  AudioRecordPlayerTogglePlay,
  AudioRecordPlayerWave,
  useAudioRecordPlayerContext,
} from "@/features/shared/audio-recorder/_components/audio-record-player";
import { convertAudioUrlToPlayable } from "@/features/shared/audio-recorder/_libs/convert-audio-url-to-playable";
import type { AudioAttachment } from "../_types/audio-attachment";

type AudioRecorderPreviewProps = ComponentProps<"div"> & {
  audioAttachment: AudioAttachment;
  autoPlay?: boolean;
  onEnded?: () => void;
  onRemove?: () => void;
};

function StopAndRemoveButton({ onRemove }: { onRemove?: () => void }) {
  const { stop } = useAudioRecordPlayerContext();
  const handleClick = useCallback(() => {
    stop();
    onRemove?.();
  }, [stop, onRemove]);
  return (
    <Button size="icon-md" variant="neutral" kind="ghost" onClick={handleClick}>
      <BwCloseLine />
    </Button>
  );
}

export function AudioRecorderPreview({
  audioAttachment,
  autoPlay,
  onEnded,
  className,
  onRemove,
  ...props
}: AudioRecorderPreviewProps) {
  if (!audioAttachment) return null;
  const isUploading = useMemo(() => {
    return audioAttachment?.status === "uploading";
  }, [audioAttachment]);

  const playableUrl = useMemo(
    () =>
      audioAttachment.localUrl ||
      convertAudioUrlToPlayable(audioAttachment.originalContentUri),
    [audioAttachment.localUrl, audioAttachment.originalContentUri],
  );

  return (
    <AudioRecordPlayer
      recordedUrl={playableUrl}
      autoPlay={autoPlay}
      onEnded={onEnded}
      className={className}
      {...props}
    >
      {isUploading ? (
        <BwLoadingLine className="text-text-primary animate-spin shrink-0 size-8" />
      ) : (
        <AudioRecordPlayerTogglePlay />
      )}

      <AudioRecordPlayerWave />
      <AudioRecordPlayerDuration />
      <StopAndRemoveButton onRemove={onRemove} />
    </AudioRecordPlayer>
  );
}
